import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

export default class extends Controller {
  static targets = ['results', 'banner', 'count', 'actionButton', 'clearButton', 'status', 'filterForm']
  static values = {
    resetUrl: String,
    reloadUrl: String,
    idsUrl: String,
    actionUrl: String,
    paramKey: String,
    returnUrl: String,
    verb: String
  }

  connect () {
    this.selection = new Set()
    this.anchorId = null
    this.applySelection()
    this.renderBanner()
    this.setupObserver()
  }

  disconnect () {
    this.observer?.disconnect()
  }

  // --- selection ---------------------------------------------------------

  toggleCell (event) {
    if (event.target.closest('.device-media-cell__preview')) return

    const id = event.currentTarget.dataset.deviceMediaId

    // Shift+click extends a contiguous range from the last clicked cell.
    if (event.shiftKey && this.anchorId) {
      this.selectRange(this.anchorId, id)
    } else {
      if (this.selection.has(id)) {
        this.selection.delete(id)
      } else {
        this.selection.add(id)
      }
      this.anchorId = id
    }

    this.applySelection()
    this.renderBanner()
  }

  selectRange (fromId, toId) {
    const ids = this.cells().map(cell => cell.dataset.deviceMediaId)
    const from = ids.indexOf(fromId)
    const to = ids.indexOf(toId)
    if (from === -1 || to === -1) return

    const [lo, hi] = from < to ? [from, to] : [to, from]
    for (let i = lo; i <= hi; i++) this.selection.add(ids[i])
  }

  cells () {
    return Array.from(this.element.querySelectorAll('[data-device-media-id]'))
  }

  // "Select all" and a group's "Add all" both source ids from the server, so they
  // cover candidates that haven't been rendered yet — the Set is the source of truth.
  async selectAll () {
    await this.addServerIds()
  }

  async selectGroup (event) {
    await this.addServerIds(event.currentTarget.dataset.groupId)
  }

  async addBracket (event) {
    const { bracketFrom, bracketTo, bracketGroupId } = event.currentTarget.dataset
    await this.addServerIds(bracketGroupId || null, bracketFrom, bracketTo)
  }

  async addServerIds (groupId = null, bracketFrom = null, bracketTo = null) {
    const body = this.filterBody()
    if (groupId) body.set('group_id', groupId)
    if (bracketFrom) body.set('bracket_from', bracketFrom)
    if (bracketTo) body.set('bracket_to', bracketTo)

    const response = await fetch(this.idsUrlValue, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
      credentials: 'same-origin'
    })
    if (!response.ok) return

    const { ids = [] } = await response.json()

    ids.forEach(id => this.selection.add(String(id)))
    this.applySelection()
    this.renderBanner()
  }

  clearSelection () {
    this.selection.clear()
    this.applySelection()
    this.renderBanner()
  }

  applySelection () {
    this.element.querySelectorAll('[data-device-media-id]').forEach(cell => {
      cell.classList.toggle('is-selected', this.selection.has(cell.dataset.deviceMediaId))
    })
  }

  // --- filter ------------------------------------------------------------

  resetFilter () {
    const frame = this.element.closest('turbo-frame')
    if (frame && this.hasResetUrlValue && this.resetUrlValue) {
      frame.src = this.resetUrlValue
    }
  }

  async refresh () {
    await this.reload()
  }

  async reload () {
    const [html, { ids: candidateIds = [] }] = await Promise.all([
      fetch(this.reloadUrlValue, { method: 'POST', headers: { Accept: 'text/html' }, body: this.filterBody(), credentials: 'same-origin' }).then(r => r.text()),
      fetch(this.idsUrlValue, { method: 'POST', headers: { Accept: 'application/json' }, body: this.filterBody(), credentials: 'same-origin' }).then(r => r.json())
    ])

    this.resultsTarget.innerHTML = html
    this.scrollParent()?.scrollTo({ top: 0 })

    const validIds = new Set(candidateIds.map(String))
    this.selection = new Set([...this.selection].filter(id => validIds.has(String(id))))

    this.applySelection()
    this.renderBanner()
    this.observeSentinels()
  }

  // --- per-group paging --------------------------------------------------

  async loadPage (sentinel) {
    const section = sentinel.closest('.device-media-group')
    const list = section.querySelector('.device-media-grid')

    const body = this.filterBody()
    body.set('group_id', sentinel.dataset.groupId)
    body.set('page', sentinel.dataset.nextPage)

    const response = await fetch(this.reloadUrlValue, {
      method: 'POST',
      headers: { Accept: 'text/html' },
      body,
      credentials: 'same-origin'
    })
    if (!response.ok) return

    list.insertAdjacentHTML('beforeend', await response.text())
    sentinel.dataset.nextPage = String(Number(sentinel.dataset.nextPage) + 1)

    const total = Number(section.dataset.total)
    const rendered = list.querySelectorAll('[data-device-media-id]').length
    if (rendered >= total) {
      this.observer?.unobserve(sentinel)
      sentinel.remove()
    }

    this.applySelection()
    this.renderBanner()
  }

  // --- infiniscroll -------------------------------------------------------

  setupObserver () {
    const root = this.scrollParent()

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        if (entry.target.dataset.loading) return
        entry.target.dataset.loading = '1'
        this.observer.unobserve(entry.target)
        this.loadPage(entry.target).then(() => {
          delete entry.target.dataset.loading
          if (entry.target.isConnected) this.observer.observe(entry.target)
        })
      })
    }, { root, rootMargin: '200px' })

    this.observeSentinels()
  }

  observeSentinels () {
    this.element.querySelectorAll('.device-media-group__sentinel').forEach(el => {
      this.observer.observe(el)
    })
  }

  scrollParent () {
    let el = this.element.parentElement
    while (el && el !== document.body) {
      const { overflow, overflowY } = getComputedStyle(el)
      if (/auto|scroll/.test(overflow + overflowY)) return el
      el = el.parentElement
    }
    return null
  }

  // --- batch -------------------------------------------------------------

  async runBatch () {
    const ids = [...this.selection]
    if (ids.length === 0) return

    this.actionButtonTarget.disabled = true
    if (this.hasStatusTarget) this.statusTarget.textContent = `${this.verbValue}ing…`

    let failedIds
    try {
      const result = await this.post(this.actionUrlValue, this.actionPayload(ids))
      failedIds = result.failed_ids || []
    } catch (error) {
      failedIds = ids
    }

    if (failedIds.length === 0) {
      Turbo.visit(this.returnUrlValue, { action: 'replace' })
      return
    }

    const done = ids.length - failedIds.length
    this.selection = new Set(failedIds) // failed items stay selected for Retry
    await this.reload()
    this.renderBanner(done, failedIds.length)
  }

  // --- banner ------------------------------------------------------------

  renderBanner (done = null, failed = null) {
    const count = this.selection.size

    if (this.hasCountTarget) this.countTarget.textContent = `${count} selected`
    if (this.hasActionButtonTarget) {
      this.actionButtonTarget.textContent = `${this.verbValue} ${count}`
      this.actionButtonTarget.disabled = count === 0
    }
    if (this.hasClearButtonTarget) this.clearButtonTarget.disabled = count === 0
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = failed ? `${done} done · ${failed} failed — Retry` : ''
    }
  }

  // --- http --------------------------------------------------------------

  filterBody () {
    const body = this.hasFilterFormTarget ? new FormData(this.filterFormTarget) : new FormData()
    body.set('authenticity_token', this.csrfToken())
    return body
  }

  // Nests the ids under the endpoint's param key.
  actionPayload (ids) {
    return { [this.paramKeyValue]: { device_media_ids: ids } }
  }

  async post (url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    })

    if (!response.ok) throw new Error(`${response.status}`)

    return response.json()
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
